import { useQueryClient } from "@tanstack/react-query";
import { Play } from "lucide-react";
import {
  useEffect,
  useRef,
  useState,
  type TouchEvent as ReactTouchEvent,
  type TransitionEvent,
} from "react";

import { useSourceUrl } from "../lib/media/detailUrls";
import { swipeDirectionOf } from "../lib/media/swipe";
import type { TimelineItem } from "../lib/media/timeline";
import { queryKeys } from "../lib/queryKeys";
import ZoomableImage from "./ZoomableImage";

/** 送りかどうか判断を始める最小の移動量。これ以下は指ぶれとみなす */
const AXIS_LOCK_PX = 8;

/** この幅の画面端から始まるタッチはブラウザの「戻る」ジェスチャーになるので奪う */
const EDGE_GUARD_PX = 32;

/** 原本が届くまでサムネイルを下敷きに出す写真。荒くても黒画面よりずっといい */
function PhotoPreview({
  src,
  placeholder,
}: {
  src: string | undefined;
  placeholder: string | undefined;
}) {
  const [loaded, setLoaded] = useState(false);
  return (
    <span className="relative flex h-full w-full items-center justify-center">
      {placeholder !== undefined && !loaded && (
        <img
          src={placeholder}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-contain"
        />
      )}
      {src !== undefined && (
        <img
          src={src}
          alt=""
          draggable={false}
          onLoad={() => {
            setLoaded(true);
          }}
          className="relative max-h-full max-w-full object-contain"
        />
      )}
    </span>
  );
}

interface SlideProps {
  media: TimelineItem | undefined;
  /** 中央に見えている1枚か。拡大や動画再生は中央だけに与える */
  active: boolean;
  poster: string | undefined;
  onZoomedChange: (zoomed: boolean) => void;
  onTap: () => void;
  /** 動画の再生・停止。PC でカーソルを隠すかどうかの判断に使う */
  onVideoPlayingChange?: (playing: boolean) => void;
}

/**
 * トラックの1枚。隣の写真は素の img、隣の動画はポスターだけを出す。
 * 実体の読み込みと再生は中央に来てから始める。
 */
function Slide({ media, active, poster, onZoomedChange, onTap, onVideoPlayingChange }: SlideProps) {
  const queryClient = useQueryClient();
  const source = useSourceUrl(media);

  if (media === undefined) {
    // 端の外側。何も無いことがそのまま見える
    return <div className="h-full w-1/3 shrink-0" />;
  }

  const url = source.data;
  const retry = () => {
    // 署名切れはここで初めて分かる。失効したこのキーだけ取り直せば復旧する
    void queryClient.invalidateQueries({ queryKey: queryKeys.mediaUrl(media.storage_key) });
  };

  return (
    <div
      className="relative flex h-full w-1/3 shrink-0 items-center justify-center"
      // 余白(レターボックス)を叩いてもバーを出し入れできる
      onClick={(e) => {
        if (active && e.target === e.currentTarget) onTap();
      }}
    >
      {/* サムネイルの下敷きがあれば、それが「読み込み中」の表示を兼ねる */}
      {active && source.isPending && poster === undefined && (
        <p className="absolute z-10 text-sm text-white/60">読み込み中...</p>
      )}
      {active && source.isError && (
        <p className="absolute z-10 text-sm text-white/60">
          {media.type === "video" ? "動画" : "写真"}の読み込みに失敗しました。
        </p>
      )}

      {media.type === "photo" &&
        (active && url !== null && url !== undefined ? (
          <ZoomableImage
            src={url}
            placeholder={poster}
            onZoomedChange={onZoomedChange}
            onTap={onTap}
            onError={retry}
          />
        ) : (
          <PhotoPreview src={url ?? undefined} placeholder={poster} />
        ))}

      {media.type === "video" &&
        (active && url !== null && url !== undefined ? (
          // 動画本体は中央に来たときにだけ読み込む(要件定義4章)。
          // key で動画ごとに要素を作り直し、前の動画の再生状態を持ち越さない。
          // 標準コントロールは動画要素の縁に付く(iOS は上端にも出る)ため、
          // 要素そのものを上下バーの内側に収める。原寸が小さくても枠いっぱいに
          // 広げるのは、幅が狭いと Chrome が全画面ボタンを出さないため
          <span className="flex h-full w-full items-center justify-center pt-[calc(3.5rem+env(safe-area-inset-top))] pb-[calc(4rem+env(safe-area-inset-bottom))]">
            <video
              key={media.id}
              src={url}
              poster={poster}
              controls
              playsInline
              preload="metadata"
              className="h-full w-full object-contain"
              onError={retry}
              // Chrome がポスターの層を残したまま映像を描かないことがある。
              // 再生が始まったら poster を外して、映像の描画を強制する
              onPlaying={(e) => {
                e.currentTarget.removeAttribute("poster");
              }}
              onPlay={() => onVideoPlayingChange?.(true)}
              onPause={() => onVideoPlayingChange?.(false)}
              onEnded={() => onVideoPlayingChange?.(false)}
            />
          </span>
        ) : (
          <span className="relative flex h-full w-full items-center justify-center pt-[calc(3.5rem+env(safe-area-inset-top))] pb-[calc(4rem+env(safe-area-inset-bottom))]">
            {poster !== undefined && (
              <img src={poster} alt="" className="h-full w-full object-contain" />
            )}
            <Play className="absolute size-12 fill-current text-white/80" aria-hidden />
          </span>
        ))}
    </div>
  );
}

interface Props {
  items: TimelineItem[];
  index: number;
  /** 拡大中は送らない。寄せて見ている最中に隣へ飛ぶのは事故にしかならない */
  zoomed: boolean;
  onZoomedChange: (zoomed: boolean) => void;
  onTap: () => void;
  onNavigate: (direction: "previous" | "next") => void;
  thumbnailUrls?: Record<string, string>;
  /** 中央の動画の再生・停止 */
  onVideoPlayingChange?: (playing: boolean) => void;
}

/**
 * 前・現在・次の3枚を横に並べ、指に追従して送るカルーセル。
 *
 * 離した位置と向きで隣へスナップするか元に戻る。実体の先読みは
 * usePrefetchNeighbours が済ませているので、隣は署名済みURLを出すだけでよい。
 */
export default function MediaCarousel({
  items,
  index,
  zoomed,
  onZoomedChange,
  onTap,
  onNavigate,
  thumbnailUrls,
  onVideoPlayingChange,
}: Props) {
  const stage = useRef<HTMLDivElement | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  // 最初にどちらへ動いたか。縦に動き始めたら、その指では送らない
  const axis = useRef<"horizontal" | "vertical" | null>(null);
  const [dragX, setDragX] = useState(0);
  // スナップ中(CSS transition 中)。次の入力は終わってから受ける
  const [settling, setSettling] = useState<"previous" | "next" | "cancel" | null>(null);

  const prev = items[index - 1];
  const current = items[index];
  const next = items[index + 1];

  const widthOf = () => stage.current?.clientWidth ?? window.innerWidth;

  // 画面端からのスワイプはブラウザの「戻る/進む」ジェスチャーになり、
  // 送りのつもりがビューアが閉じてしまう。端で始まったタッチはここで奪う。
  // React の touchstart は passive で付き preventDefault が効かないため、自前で張る
  useEffect(() => {
    const node = stage.current;
    if (node === null) return;
    const onEdgeTouch = (event: globalThis.TouchEvent) => {
      const touch = event.touches[0];
      if (touch === undefined) return;
      if (touch.clientX < EDGE_GUARD_PX || touch.clientX > window.innerWidth - EDGE_GUARD_PX) {
        event.preventDefault();
      }
    };
    node.addEventListener("touchstart", onEdgeTouch, { passive: false });
    return () => {
      node.removeEventListener("touchstart", onEdgeTouch);
    };
  }, []);

  /** 端では抵抗を付ける。それ以上先が無いことが指に伝わる */
  const restrained = (dx: number) => {
    const blocked = (dx > 0 && prev === undefined) || (dx < 0 && next === undefined);
    return blocked ? dx / 3 : dx;
  };

  function onTouchStart(e: ReactTouchEvent<HTMLDivElement>) {
    const touch = e.changedTouches[0];
    // 2本以上はピンチ。送りとして拾わない
    if (zoomed || settling !== null || e.touches.length > 1 || touch === undefined) {
      start.current = null;
      return;
    }
    axis.current = null;
    start.current = { x: touch.clientX, y: touch.clientY };
  }

  function onTouchMove(e: ReactTouchEvent<HTMLDivElement>) {
    const from = start.current;
    const touch = e.touches[0];
    if (from === null || touch === undefined) return;
    const dx = touch.clientX - from.x;
    const dy = touch.clientY - from.y;

    if (axis.current === null) {
      if (Math.abs(dx) < AXIS_LOCK_PX && Math.abs(dy) < AXIS_LOCK_PX) return;
      axis.current = Math.abs(dx) >= Math.abs(dy) ? "horizontal" : "vertical";
    }
    if (axis.current === "vertical") return;
    setDragX(restrained(dx));
  }

  function onTouchEnd(e: ReactTouchEvent<HTMLDivElement>) {
    const from = start.current;
    const touch = e.changedTouches[0];
    start.current = null;
    if (from === null || touch === undefined || axis.current !== "horizontal") return;

    const direction = swipeDirectionOf(touch.clientX - from.x, touch.clientY - from.y);
    const target = direction === "next" ? next : direction === "previous" ? prev : undefined;
    if (direction !== null && target !== undefined) {
      // 隣まで滑らせ、滑り終わってから切り替える
      setSettling(direction);
      setDragX(direction === "next" ? -widthOf() : widthOf());
    } else if (dragX !== 0) {
      setSettling("cancel");
      setDragX(0);
    }
  }

  function onTrackTransitionEnd(e: TransitionEvent<HTMLDivElement>) {
    if (e.target !== e.currentTarget) return;
    if (settling === "previous" || settling === "next") onNavigate(settling);
    // 切り替えと同時に真ん中へ戻す。新しい中央は今まで見えていた1枚なので、絵は動かない
    setSettling(null);
    setDragX(0);
  }

  return (
    <div
      ref={stage}
      className="absolute inset-0 overflow-hidden"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
    >
      <div
        className="flex h-full w-[300%]"
        style={{
          transform: `translateX(calc(-100% / 3 + ${String(dragX)}px))`,
          transition: settling !== null ? "transform 260ms cubic-bezier(0.32, 0.72, 0, 1)" : "none",
        }}
        onTransitionEnd={onTrackTransitionEnd}
      >
        <Slide
          key={prev?.id ?? "edge-previous"}
          media={prev}
          active={false}
          poster={prev === undefined ? undefined : thumbnailUrls?.[prev.thumbnail_key]}
          onZoomedChange={onZoomedChange}
          onTap={onTap}
        />
        <Slide
          key={current?.id ?? "edge-current"}
          media={current}
          active
          poster={current === undefined ? undefined : thumbnailUrls?.[current.thumbnail_key]}
          onZoomedChange={onZoomedChange}
          onTap={onTap}
          onVideoPlayingChange={onVideoPlayingChange}
        />
        <Slide
          key={next?.id ?? "edge-next"}
          media={next}
          active={false}
          poster={next === undefined ? undefined : thumbnailUrls?.[next.thumbnail_key]}
          onZoomedChange={onZoomedChange}
          onTap={onTap}
        />
      </div>
    </div>
  );
}
